/**
 * Support queue hooks. Mutations invalidate the summary too — the nav badge
 * counts the same rows.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';

import { supportApi } from '@/lib/api/support.api';
import type {
  AdminCreateSupportMessageRequest,
  AdminSupportSummary,
  AdminSupportTicketFilters,
  AdminSupportTicketsResponse,
  SupportTicketAdminDetail,
  UpdateSupportTicketRequest,
} from '@repo/types';

// ==================== Query Keys ====================

export const supportKeys = {
  all: ['support'] as const,
  tickets: (filters?: AdminSupportTicketFilters) =>
    [...supportKeys.all, 'tickets', filters] as const,
  ticket: (id: string) => [...supportKeys.all, 'ticket', id] as const,
  summary: () => [...supportKeys.all, 'summary'] as const,
};

// ==================== Queries ====================

export function useSupportTickets(
  filters?: AdminSupportTicketFilters,
  options?: Partial<UseQueryOptions<AdminSupportTicketsResponse, Error>>
) {
  return useQuery<AdminSupportTicketsResponse, Error>({
    queryKey: supportKeys.tickets(filters),
    queryFn: () => supportApi.getTickets(filters),
    ...options,
  });
}

export function useSupportTicket(
  id: string | null,
  options?: Partial<UseQueryOptions<SupportTicketAdminDetail, Error>>
) {
  return useQuery<SupportTicketAdminDetail, Error>({
    queryKey: supportKeys.ticket(id ?? ''),
    queryFn: () => supportApi.getTicket(id as string),
    enabled: Boolean(id),
    ...options,
  });
}

export function useSupportSummary(
  options?: Partial<UseQueryOptions<AdminSupportSummary, Error>>
) {
  return useQuery<AdminSupportSummary, Error>({
    queryKey: supportKeys.summary(),
    queryFn: () => supportApi.getSummary(),
    // The nav badge is ambient. Refresh it on focus, do not poll behind the
    // operator's back.
    staleTime: 60_000,
    ...options,
  });
}

// ==================== Mutations ====================

export function useAddSupportMessage(id: string) {
  const queryClient = useQueryClient();

  return useMutation<SupportTicketAdminDetail, Error, AdminCreateSupportMessageRequest>({
    mutationFn: (payload) => supportApi.addMessage(id, payload),
    onSuccess: (ticket, variables) => {
      queryClient.setQueryData(supportKeys.ticket(id), ticket);
      queryClient.invalidateQueries({ queryKey: supportKeys.all });
      toast.success(
        variables.visibility === 'internal' ? 'Internal note added' : 'Reply sent to the customer'
      );
    },
    onError: (error) => toast.error(messageOf(error, 'Could not post the message')),
  });
}

export function useUpdateSupportTicket(id: string) {
  const queryClient = useQueryClient();

  return useMutation<SupportTicketAdminDetail, Error, UpdateSupportTicketRequest>({
    mutationFn: (payload) => supportApi.updateTicket(id, payload),
    onSuccess: (ticket) => {
      queryClient.setQueryData(supportKeys.ticket(id), ticket);
      queryClient.invalidateQueries({ queryKey: supportKeys.all });
      toast.success('Ticket updated');
    },
    onError: (error) => toast.error(messageOf(error, 'Could not update the ticket')),
  });
}

export function useDeleteSupportTicket() {
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, Error, string>({
    mutationFn: (id) => supportApi.deleteTicket(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportKeys.all });
      toast.success('Ticket deleted');
    },
    onError: (error) => toast.error(messageOf(error, 'Could not delete the ticket')),
  });
}

/** `client.ts` already surfaces 403s; this covers everything else. */
function messageOf(error: Error, fallback: string): string {
  const response = (error as unknown as { response?: { data?: { message?: string } } }).response;
  return response?.data?.message ?? fallback;
}
